import React, { useEffect, useRef, useState } from 'react';

import {
    addButton,
    bottom,
    bottomCard,
    bottomOpen,
    bottomText,
    bottomTitle,
    checkIcon,
    closeIcon,
    container,
    detailName,
    detailOption,
    detailOptionButton,
    detailOptionCell,
    detailOptionGrid,
    detailOptionInfo,
    detailOptions,
    details,
    forWhoButton,
    header,
    infoIcon,
    initialOverflow,
    maxPrice,
    name,
    nameMobile,
    oneTime,
    popular,
    price,
    priceBox,
    priceInfo,
    prices,
    ribbon,
    table,
    titleBox,
    top,
    topCard,
    description,
} from './pricing-cards.module.scss';
import ChevronIcon from '../../assets/images/svg/chevron-down.svg';
import CheckIcon from '../../assets/images/svg/ok.svg';
import CloseIcon from '../../assets/images/svg/icon-close.svg';
import InfoIcon from '../../assets/images/svg/info-transparent.svg';
import useCustomAddToCart from '../../hooks/use-custom-add-to-cart';
import { useSelector } from 'react-redux';
import { selectCartIsLoading } from '../../redux/shop/cart/cart.selectors';
import { getAddToCartRedirectUrl } from '../../utills/get-add-to-cart-redirect-url';

import Button from '../atoms/button';
import Markdown from '../hoc/markdown';

interface IPricingCardProductVariant {
    variantId: number;
    price: number;
    maxPrice: number;
}

interface IPricingCardsProps {
    className?: string;
    TitleTag?: React.ElementType;
    productId: number;
    isOneTimePayment: boolean;
    standard: IPricingCardProductVariant;
    premium: IPricingCardProductVariant;
    premiumPlus: IPricingCardProductVariant;
}

const PricingCards: React.FC<IPricingCardsProps> = ({
    className = '',
    TitleTag = 'h3',
    productId,
    standard,
    premium,
    premiumPlus,
    isOneTimePayment,
}) => {
    const { addToCart } = useCustomAddToCart(productId);
    const isCartLoading = useSelector(selectCartIsLoading);
    const [isBottomOpen, setIsBottomOpen] = useState(true);
    const headerRef = useRef<HTMLDivElement>(null);
    const [headerHeight, setHeaderHeight] = useState(0);

    const handleToggleBottom = () => {
        setIsBottomOpen((prev) => !prev);
    };

    const handleAddToCart = (variantId: number) => {
        return () => {
            const redirectUrl = getAddToCartRedirectUrl();
            addToCart({ productId, variantId }, { redirectUrl });
        };
    };

    useEffect(() => {
        const handleResize = () => {
            setHeaderHeight(getPricingHeaderHeight(headerRef.current));
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [isBottomOpen, standard, premium, premiumPlus]);

    useEffect(() => {
        document.body.classList.add(initialOverflow);

        return () => {
            document.body.classList.remove(initialOverflow);
        };
    }, []);

    return (
        <div className={`${container} ${className} ${isOneTimePayment ? oneTime : ''}`}>
            <div ref={headerRef} className={`${header} ${isBottomOpen ? bottomOpen : ''}`}>
                <div className={top}>
                    {[standard, premium, premiumPlus].map((variant, index) => {
                        const isPopular = cards[index].isPopular;
                        return (
                            <div className={`${topCard} ${isPopular ? popular : ''}`}>
                                <div className={ribbon}>{isPopular ? copy.mostPopular : ''}</div>
                                <div className={titleBox}>
                                    <TitleTag className={name}>{cards[index].name}</TitleTag>
                                    <TitleTag className={nameMobile}>
                                        {cards[index].nameMobile}
                                    </TitleTag>
                                </div>
                                <Markdown className={description}>
                                    {cards[index].description}
                                </Markdown>
                                <div className={priceBox}>
                                    <div className={prices}>
                                        {variant.maxPrice > variant.price && (
                                            <p className={maxPrice}>
                                                {Number.isInteger(variant.maxPrice)
                                                    ? variant.maxPrice
                                                    : variant.maxPrice.toFixed(2)}{' '}
                                                zł
                                            </p>
                                        )}
                                        <p className={price}>
                                            {Number.isInteger(variant.price)
                                                ? variant.price
                                                : variant.price.toFixed(2)}{' '}
                                            zł
                                        </p>
                                    </div>
                                    <p className={priceInfo}>{copy.installmentValue}</p>
                                </div>
                                <Button
                                    className={addButton}
                                    color="yellow"
                                    onClick={handleAddToCart(variant.variantId)}
                                    disabled={isCartLoading}
                                >
                                    {copy.addToCart}
                                </Button>
                            </div>
                        );
                    })}
                </div>
                <button onClick={handleToggleBottom} className={forWhoButton}>
                    {copy.forWhoButton} <ChevronIcon />
                </button>
                <div className={bottom}>
                    {[standard, premium, premiumPlus].map((variant, index) => {
                        return (
                            <div className={bottomCard}>
                                <p className={bottomTitle}>{copy.forWhoTitle}</p>
                                <p className={bottomText}>{cards[index].forWho}</p>
                            </div>
                        );
                    })}
                </div>
            </div>
            <div
                className={table}
                style={{
                    height: `calc(100vh - var(--header-height--short) - ${headerHeight - 1}px)`,
                }}
            >
                {packageDetails.map((detail) => {
                    return (
                        <div className={details}>
                            <p className={detailName}>{detail.label}</p>
                            <div className={detailOptions}>
                                {detail.options.map((option) => {
                                    return (
                                        <div>
                                            <details className={detailOption}>
                                                <summary className={detailOptionButton}>
                                                    <span>{option.label}</span>
                                                    <InfoIcon className={infoIcon} />
                                                </summary>
                                                <p className={detailOptionInfo}>{option.info}</p>
                                            </details>
                                            <div className={detailOptionGrid}>
                                                {option.package.map((item) => {
                                                    return (
                                                        <p className={detailOptionCell}>
                                                            {typeof item === 'string' && item}
                                                            {item === true && (
                                                                <CheckIcon className={checkIcon} />
                                                            )}
                                                            {!item && (
                                                                <CloseIcon className={closeIcon} />
                                                            )}
                                                        </p>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

function getPricingHeaderHeight(element: HTMLDivElement | null) {
    if (!element) return 0;
    return element.offsetHeight;
}

const copy = {
    installmentValue: 'miesięcznie',
    addToCart: 'Inwestuję w siebie',
    forWhoTitle: 'Wybierz ten pakiet jeżeli',
    more: 'Więcej szczegółów',
    mostPopular: 'Najpopularniejszy',
    forWhoButton: 'Dla kogo',
};

const cards = [
    {
        name: 'Program Standard',
        nameMobile: 'Program Standard',
        description:
            '<ul><li>aż 3 ekspertów pracuje na Twoje efekty,</li><li>masz nielimitowany kontakt,</li><li>personalizujemy dla Ciebie dietę i ćwiczenia,</li><li>uczymy zarządzania stresem i emocjami oraz budowania nawyków.</li></ul>',
        forWho:
            'zależy Ci na możliwie najniższej cenie oraz jednocześnie najlepszej opiece dietetyczno-treningowej. Dostaniesz wszystko czego potrzebujesz, aby osiągnąć swoje cele.',
    },
    {
        name: 'Program Premium',
        nameMobile: 'Program Premium',
        description:
            '<ul><li>dostajesz wszystko to, co w „Programie Standard” oraz:</li><li>szybszy kontakt,</li><li>szybsze przygotowanie planów diety i ćwiczeń,</li><li>więcej jadłospisów, planów treningowych i przepisów,</li><li>dodatkowe dostępy do specjalnych programów treningowych, wyzwań, ebooków i kursów.</li></ul>',
        isPopular: true,
        forWho:
            'zależy Ci na szybszym kontakcie ze swoim opiekunem oraz chcesz częściej dostawać nowe jadłospisy i plany treningowe. W ciągle niskiej cenie pakietu otrzymujesz dodatkowe dostępy do specjalnych programów treningowych, wyzwań, ebooków oraz kursów.',
    },
    {
        name: 'Program Premium+',
        nameMobile: 'Program Premium+',
        description:
            '<ul><li>dostajesz wszystko to, co w „Programie Premium” oraz:</li><li>priorytetowy kontakt i obsługę,</li><li>systematyczne konsultacje telefoniczne,</li><li>kontakt SMS z opiekunem,</li><li>pełny dostęp do wszystkiego, co zawiera platforma.</li></ul>',
        forWho:
            'zależy Ci na możliwie najszybszym kontakcie ze swoim opiekunem oraz możliwością rozmów video/telefonicznych z nim. W tym pakiecie dodatkowo dostajesz możliwość komunikacji przez SMSy oraz pełny dostęp do wszystkiego co zawiera nasza platforma.',
    },
];

const packageDetails = [
    {
        label: '🌮Dieta',
        options: [
            {
                label: 'Spersonalizowane menu na 7 dni',
                info:
                    'Otrzymasz od nas dostosowany do Ciebie, 7-dniowy jadłospis. Będzie on stworzony na podstawie wypełnionej przez Ciebie ankiety, analizy wyników badań krwi oraz dotychczasowego wywiadu.',
                package: ['co miesiąc', 'co 2 tygodnie', 'co tydzień'],
            },
            {
                label: 'Elastyczna dieta',
                info:
                    'Personaliacja diety to nie tylko dopasowanie jej do Twoich potrzeb, celów i stanu zdrowia. My dostosujemy ją również do Twojego stylu życia!',
                package: [true, true, true],
            },
            {
                label: 'Zamienniki posiłków i produktów',
                info:
                    'My dopasujemy do Ciebie jadłospis, jednocześnie dając Ci duże możliwości modyfikacji. Możesz zmieniać posiłki i produkty, jeżeli masz taką potrzebę.',
                package: [true, true, true],
            },
            {
                label: 'Eliminacja produktów nielubianych',
                info:
                    'Każdy z nas czegoś nie lubi lub nie może jeść, dlatego też będziemy eliminować to z Twojego jadłospisu. Dieta musi być dopasowana do Ciebie, a nie Ty do niej.',
                package: [true, true, true],
            },
            {
                label: 'Dostęp do ponad 400 przepisów',
                info:
                    'Nasza baza pysznych i zdrowych przepisów ciągle rośnie! Mamy nadzieję, że tak jak nasi podopieczni również pokochasz różnorodność i smak naszej diety!',
                package: [true, true, true],
            },
            {
                label: 'Lista zakupów',
                info:
                    'Twoja wygodna lista zakupowa generowana dla Twojego menu, dostępna w pliku PDF lub aplikacji mobilnej- zawsze pod ręką!',
                package: [true, true, true],
            },
            {
                label: 'Poradnik skutecznego odżywiania',
                info:
                    'Poznaj wszystkie sekrety zdrowego stylu życia, dzięki którym m.in. łatwej schudniesz i poczujesz się świetnie!',
                package: [true, true, true],
            },
            {
                label: 'Kalendarz posiłków',
                info:
                    'Nasz specjalny planer posiłków w formie kalendarza ułatwi Ci korzystanie oraz planowanie swojej diety.',
                package: [true, true, true],
            },
            {
                label: 'Plan suplementacji',
                info:
                    'W razie potrzeby przygotujemy dla Ciebie również plan suplementacji dostosowany do Twoich celów, stanu zdrowia i potrzeb.',
                package: [true, true, true],
            },
            {
                label: 'Możliwość dodawania swoich przepisów',
                info:
                    'Masz swoje przepisy, które są zdrowe lub trzeba je lekko zmodyfikować aby były zdrowsze? Możesz z nich korzystać na naszej platformie!',
                package: [true, true, true],
            },
            {
                label: 'Dieta wygodna dla pary',
                info:
                    'Chcesz razem ze swoim partnerem zmienić swoje odżywianie? Brawo! My dopasujemy jadłospis do Waszych potrzeb m.in. tak, abyście razem mogli jeść podobne posiłki.',
                package: [true, true, true],
            },
        ],
    },
    {
        label: '🤝Opieka',
        options: [
            {
                label: 'Czas odpowiedzi opiekuna',
                info:
                    'jesteśmy po to, aby Ci pomóc! Pytaj o wszystko bez limitów, aby jak najwięcej zrozumieć i mieć jak najlepsze efekty!',
                package: [
                    'w dni robocze do 72 godzin',
                    'w dni robocze do 48 godzin',
                    'w dni robocze do 24 godzin',
                ],
            },
            {
                label: 'Gwarantowane efekty albo zwrot kosztów',
                info:
                    'Jeżeli stosujesz się do naszych zasad przez conajmniej 30 dni i coś Ci się nie spodoba, to możesz zrezygnować ze współpracy. Więcej informacji w regulaminie serwisu',
                package: [true, true, true],
            },
            {
                label: 'Indywidualna Opieka',
                info:
                    'Będziesz mieć nie tylko swojego osobistego opiekuna, ale w razie potrzeby cały nasz zespół ekspertów do dyspozycji. Twoje zadowolenie i efekty są dla nas najważniejsze.',
                package: [true, true, true],
            },
            {
                label: 'Nielimitowany kontakt online',
                info:
                    'Już nie musisz się ograniczać! Pytaj, kiedy chcesz i o co chcesz przez nasz specjalny komunikator w aplikacji i stronie webowej.',
                package: [true, true, true],
            },
            {
                label: 'Dostęp do zamkniętej grupy',
                info:
                    'Dołącz do naszej zamkniętej społeczności podopiecznych, którzy się motywują i inspirują we wspólnych działaniach!',
                package: [true, true, true],
            },
            {
                label: 'Omawianie raportów i postępów',
                info:
                    'Każdy Twój raport zostanie dokładnie sprawdzony i omówiony przez Twojego opiekuna, aby zapewnić Ci najlepsze efekty.',
                package: [true, true, true],
            },
            {
                label: 'Dodatkowy kontakt SMS',
                info:
                    'będziesz w stałym kontakcie ze swoim opiekunem również przez SMSy, rozmawiajmy tak, jak Ci wygodnie.',
                package: [false, false, true],
            },
            {
                label: 'Konsultacje video lub telefoniczne',
                info:
                    'co 2 tygodnie możemy omówić Twoje postępy w formie rozmowy telefonicznej lub video.',
                package: [false, false, true],
            },
        ],
    },
    {
        label: '🏋️‍♂️Trening',
        options: [
            {
                label: 'Nowy spersonalizowany plan treningowy',
                info: 'Stworzymy dla Ciebie plan treningowy, który zoptymalizuje Twoje efekty.',
                package: ['co 8 tygodni', 'co 6 tygodni', 'co 4 tygodnie'],
            },
            {
                label: 'Elastyczne trenowanie',
                info:
                    'Dopasujemy ćwiczenia do Twojego stylu życia, ilości czasu, stanu zdrowia, poziomu zaawansowania, dostępnego sprzętu i miejsca.',
                package: [true, true, true],
            },
            {
                label: 'Modyfikacja treningu',
                info:
                    'Trening dostosujemy do Ciebie i jeżeli coś Ci się nie spodoba, to go zmodyfikujemy. Ma być idealny dla Ciebie.',
                package: [true, true, true],
            },
            {
                label: 'Ćwiczenia w formie video + opis',
                info:
                    'Wszystkie ćwiczenia są pokazane w formie video oraz dokładnie opisane. Prawidłowa technika jest kluczowa ze względu na efekty i bezpieczeństwo.',
                package: [true, true, true],
            },
            {
                label: 'Treningi z Anią lub Bartkiem',
                info: 'Dowolnie wybieraj, z kim chcesz ćwiczyć.',
                package: [true, true, true],
            },
            {
                label: 'Analiza techniki ćwiczeń',
                info:
                    'Możesz przesłać nam film z danym ćwiczeniem, a my podpowiemy, czy wykonujesz je właściwie oraz co można poprawić.',
                package: [false, true, true],
            },
            {
                label: 'Dostęp do wyzwań treningowych',
                info:
                    'Systematycznie tworzymy wyzwania treningowe, które świetnie motywują całą naszą społeczność do wspólnego działania!',
                package: ['niektóre', 'wszystkie', 'wszystkie'],
            },
        ],
    },
    {
        label: '🎁Dodatkowo',
        options: [
            {
                label: 'Dostęp do materiałów edukacyjnych',
                info:
                    'Przygotowaliśmy mnóstwo dodatkowych materiałów edukacyjnych, dzięki którym lepiej zrozumiesz, jak prowadzić zdrowy styl życia i mieć lepsze efekty!',
                package: [true, true, true],
            },
            {
                label: 'Nagrody za aktywność',
                info:
                    'Będziesz dostawać punkty (Ketony) za aktywność, które potem możesz wymienić na nagrody i zniżki w naszym sklepie',
                package: [true, true, true],
            },
            {
                label: 'Konsultacje video lub telefoniczne',
                info:
                    'co 2 tygodnie możemy omówić Twoje postępy w formie rozmowy telefonicznej lub video.',
                package: [false, false, true],
            },
            {
                label: 'Dostęp do wyzwań',
                info:
                    'Organizujemy różne wyzwania dla naszej społeczności, razem jeszcze łatwiej i przyjemniej osiągać kolejne cele.',
                package: ['niektóre', 'wszystkie', 'wszystkie'],
            },
            {
                label: 'Dostęp do ebooków',
                info:
                    'Mamy mnóstwo ebooków z dodatkowymi przepisami m.in. "Keto Słodycze", "Keto Adaptacja" czy tematyczne jak "Post Przerywany" czy "O chorobie Hashimoto"',
                package: ['niektóre', 'wszystkie', 'wszystkie'],
            },
            {
                label: 'Dostęp do kursów ',
                info:
                    'edukacja to podstawa dlatego przygotowaliśmy specjalne kursy, które w przystępny sposób wyjaśniają trudne zagadnienia.',
                package: ['niektóre', 'wszystkie', 'wszystkie'],
            },
            {
                label: 'Dostęp do programów treningowych',
                info:
                    'Potrzebujesz różnorodności? Stworzyliśmy specjalne, bardzo skuteczne programy treningowe, które możesz wykonywać, kiedy tylko chcesz!',
                package: ['niektóre', 'niektóre', 'wszystkie'],
            },
        ],
    },
];

export default PricingCards;
